import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    FileInput,
    Group,
    Image,
    Modal,
    Stack,
    Tabs,
    Text,
    TextInput,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import { IconEraser } from '@tabler/icons-react';
import '@fontsource/dancing-script';
import '@fontsource/great-vibes';
import '@fontsource/sacramento';
import {
    deriveInitials,
    renderTextSignatureToPng,
    SIGNATURE_STYLES,
    trimTransparentPng,
} from './signatureImage.ts';

export interface AdoptedSignature {
    signaturePng: string;
    initialsPng: string;
}

interface SignatureAdoptModalProps {
    opened: boolean;
    onClose: () => void;
    defaultName?: string;
    onAdopt: (adopted: AdoptedSignature) => void;
}

type SignTab = 'style' | 'draw' | 'upload';

const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const SignatureAdoptModal: React.FC<SignatureAdoptModalProps> = ({
    opened,
    onClose,
    defaultName = '',
    onAdopt,
}) => {
    const { t } = useTranslation();
    const [fullName, setFullName] = useState(defaultName);
    const [initials, setInitials] = useState(deriveInitials(defaultName));
    const [tab, setTab] = useState<SignTab>('style');
    const [styleIndex, setStyleIndex] = useState(0);
    const [uploadedPng, setUploadedPng] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const sigPadRef = useRef<SignatureCanvas | null>(null);
    const initPadRef = useRef<SignatureCanvas | null>(null);

    useEffect(() => {
        if (!opened) return;
        setFullName(defaultName);
        setInitials(deriveInitials(defaultName));
        setTab('style');
        setUploadedPng(null);
        setError(null);
    }, [opened, defaultName]);

    const style = SIGNATURE_STYLES[styleIndex];

    const buildStylePng = (text: string) =>
        renderTextSignatureToPng(text, style.fontFamily);

    const handleAdopt = async () => {
        setError(null);
        if (!fullName.trim()) {
            setError(t('eicf.signature.nameRequired'));
            return;
        }
        if (!initials.trim()) {
            setError(t('eicf.signature.initialsRequired'));
            return;
        }
        try {
            let signaturePng: string;
            if (tab === 'draw') {
                if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
                    setError(t('eicf.signature.signatureRequired'));
                    return;
                }
                signaturePng = sigPadRef.current
                    .getCanvas()
                    .toDataURL('image/png');
            } else if (tab === 'upload') {
                if (!uploadedPng) {
                    setError(t('eicf.signature.uploadRequired'));
                    return;
                }
                signaturePng = uploadedPng;
            } else signaturePng = await buildStylePng(fullName);

            const initialsPng =
                tab === 'draw' &&
                initPadRef.current &&
                !initPadRef.current.isEmpty()
                    ? initPadRef.current.getCanvas().toDataURL('image/png')
                    : await buildStylePng(initials || deriveInitials(fullName));

            // Crop transparent margins so each image fills its target field box.
            const [trimmedSig, trimmedInit] = await Promise.all([
                trimTransparentPng(signaturePng),
                trimTransparentPng(initialsPng),
            ]);

            onAdopt({
                signaturePng: trimmedSig.dataUrl,
                initialsPng: trimmedInit.dataUrl,
            });
            onClose();
        } catch {
            setError(t('eicf.signature.unexpectedError'));
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="lg"
            title={t('eicf.signature.adoptTitle')}
        >
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    {t('eicf.signature.adoptSubtitle')}
                </Text>
                {error && (
                    <Text size="sm" c="red">
                        {error}
                    </Text>
                )}
                <Group grow align="flex-start">
                    <TextInput
                        label={t('eicf.signature.fullName')}
                        value={fullName}
                        onChange={(e) => setFullName(e.currentTarget.value)}
                        required
                    />
                    <TextInput
                        label={t('eicf.signature.initials')}
                        value={initials}
                        onChange={(e) => setInitials(e.currentTarget.value)}
                        required
                    />
                </Group>

                <Tabs value={tab} onChange={(v) => setTab((v as SignTab) ?? 'style')}>
                    <Tabs.List>
                        <Tabs.Tab value="style">
                            {t('eicf.signature.tabStyle')}
                        </Tabs.Tab>
                        <Tabs.Tab value="draw">
                            {t('eicf.signature.tabDraw')}
                        </Tabs.Tab>
                        <Tabs.Tab value="upload">
                            {t('eicf.signature.tabUpload')}
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="style" pt="md">
                        <Group justify="space-between" mb="xs">
                            <Text size="xs" c="dimmed">
                                {t('eicf.signature.preview')}
                            </Text>
                            <Button
                                variant="subtle"
                                size="xs"
                                onClick={() =>
                                    setStyleIndex(
                                        (styleIndex + 1) % SIGNATURE_STYLES.length,
                                    )
                                }
                            >
                                {t('eicf.signature.changeStyle')}
                            </Button>
                        </Group>
                        <Group
                            grow
                            style={{
                                border: '1px solid var(--mantine-color-gray-3)',
                                borderRadius: 4,
                                padding: 16,
                            }}
                        >
                            <Text style={{ fontFamily: style.fontFamily, fontSize: 40 }}>
                                {fullName}
                            </Text>
                            <Text style={{ fontFamily: style.fontFamily, fontSize: 40 }}>
                                {initials}
                            </Text>
                        </Group>
                    </Tabs.Panel>

                    <Tabs.Panel value="draw" pt="md">
                        <Group align="flex-start">
                            <Stack gap={4}>
                                <Text size="xs" c="dimmed">
                                    {t('eicf.signature.fullName')}
                                </Text>
                                {/* Canvas drawing-buffer size MUST equal its displayed
                                    size, or signature_pad offsets/clips strokes. */}
                                <Box
                                    style={{
                                        border: '1px solid var(--mantine-color-gray-4)',
                                        borderRadius: 4,
                                        width: 320,
                                        height: 120,
                                    }}
                                >
                                    <SignatureCanvas
                                        ref={sigPadRef}
                                        penColor="#1a1a6e"
                                        canvasProps={{ width: 320, height: 120 }}
                                    />
                                </Box>
                                <Button
                                    variant="subtle"
                                    size="xs"
                                    leftSection={<IconEraser size={14} />}
                                    onClick={() => sigPadRef.current?.clear()}
                                >
                                    {t('eicf.signature.clearSignature')}
                                </Button>
                            </Stack>
                            <Stack gap={4}>
                                <Text size="xs" c="dimmed">
                                    {t('eicf.signature.initials')}
                                </Text>
                                <Box
                                    style={{
                                        border: '1px solid var(--mantine-color-gray-4)',
                                        borderRadius: 4,
                                        width: 200,
                                        height: 120,
                                    }}
                                >
                                    <SignatureCanvas
                                        ref={initPadRef}
                                        penColor="#1a1a6e"
                                        canvasProps={{ width: 200, height: 120 }}
                                    />
                                </Box>
                                <Button
                                    variant="subtle"
                                    size="xs"
                                    leftSection={<IconEraser size={14} />}
                                    onClick={() => initPadRef.current?.clear()}
                                >
                                    {t('eicf.signature.clearInitials')}
                                </Button>
                            </Stack>
                        </Group>
                    </Tabs.Panel>

                    <Tabs.Panel value="upload" pt="md">
                        <Stack gap="xs">
                            <FileInput
                                label={t('eicf.signature.uploadLabel')}
                                accept="image/png,image/jpeg"
                                onChange={async (file) => {
                                    if (file)
                                        setUploadedPng(await readFileAsDataUrl(file));
                                }}
                            />
                            {uploadedPng && (
                                <Image
                                    src={uploadedPng}
                                    alt={t('eicf.signature.preview')}
                                    h={120}
                                    fit="contain"
                                />
                            )}
                        </Stack>
                    </Tabs.Panel>
                </Tabs>

                <Text size="xs" c="dimmed">
                    {t('eicf.signature.legal')}
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose}>
                        {t('button.cancel')}
                    </Button>
                    <Button color="green" onClick={handleAdopt}>
                        {t('eicf.signature.adopt')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default SignatureAdoptModal;
